import { useCallback, useContext, useEffect, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { createSelectChatJidByDriverJid } from "@/reducers/accountChatsSelectors"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { selectAccountChatsAsList } from "@/reducers/accountChatsSlice"
import { joinChat, updateDriverProfile } from "@/reducers/chatsSliceThunks"
import { XmppContext } from "@/stream/xmppClient"
import { addBookmark } from "@/api/xeps/PepNativeBookmarks"
import ChatHelpers from "@/lib/chatHelpers"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import { AccountChat } from "@/lib/accountChatsParser"
import { Chat } from "@/reducers/chatsSlice"
import { Profile } from "@/reducers/profilesSlice"

type DriverButtonData = {
  avatarName: string
  chatName: string
  avatarColor: string
  driver?: Profile
  onClick: () => void
} | null

export const useDriverButtonData = (
  driverJid: string | undefined,
  isSharedDriver: boolean,
  onActiveChatChange: (chatJid: string) => void,
  /** The room to open, when the caller already knows it. */
  targetChatJid?: string,
  /** Falls back to labelling the button when that room has no driver. */
  targetChatName?: string,
): DriverButtonData => {
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()

  const chatJidSelector = useMemo(
    () => createSelectChatJidByDriverJid(driverJid ?? ""),
    [driverJid],
  )

  const lookedUpChat = useAppSelector(chatJidSelector)
  const chats = useAppSelector(selectChats)
  const accountChats = useAppSelector(selectAccountChatsAsList)
  const driver = useAppSelector(findProfile(driverJid ?? ""))

  useEffect(() => {
    if (driverJid && !driver) {
      dispatch(updateDriverProfile(client, driverJid))
    }
  }, [client, dispatch, driver, driverJid])

  // A caller holding the room wins over looking one up by driver: that lookup
  // takes the first room the driver sits in, which is the wrong one as soon as
  // they sit in two.
  const targetJid = targetChatJid ?? lookedUpChat?.chatJid
  const joinedChat = targetJid
    ? chats.find((chat) => chat.jid === targetJid)
    : undefined
  const shouldJoin = Boolean(targetJid) && !joinedChat
  const chat =
    joinedChat ??
    (shouldJoin
      ? accountChats.find((chat) => chat.jid === targetJid && !chat.hidden)
      : undefined)

  const onClick = useCallback(async () => {
    if (!chat) return null
    if (shouldJoin) {
      await addBookmark(client, chat.jid, chat.name)
      window.analytics.track("AddBookmark", {
        chatJid: chat?.jid,
        chatName: chat?.name,
        from: "CoDriverButton",
      })
    }
    dispatch(joinChat(client, chat))
    onActiveChatChange(chat.jid)
  }, [chat, shouldJoin, client, dispatch, onActiveChatChange])

  // A room with an empty seat is still worth offering - the office crosses to
  // it by vehicle, not by driver.
  if (!chat) return null

  const getAvatarColor = () => {
    if (shouldJoin) {
      return (
        ChatHelpers.getAccountChatColor(chat as AccountChat) ||
        "var(--defaultAvatarColor)"
      )
    }

    const chatColor = ChatHelpers.getChatColor(chat as Chat)
    if (chatColor) return chatColor
    return isSharedDriver ? "#ffecb8" : "var(--defaultAvatarColor)"
  }

  const formatDriverName = () => {
    if (!driver) {
      const name = targetChatName || chat.name || ""
      return { chatName: name, avatarName: name }
    }
    const firstName = driver.firstName || ""
    const lastName = driver.lastName || ""
    return {
      chatName: `${firstName.charAt(0).toUpperCase()}. ${lastName}`,
      avatarName: `${firstName} ${lastName}`,
    }
  }

  const { chatName, avatarName } = formatDriverName()
  const avatarColor = getAvatarColor()

  return {
    driver,
    avatarName,
    chatName,
    avatarColor,
    onClick,
  }
}
